// src/utils/nativeOAuth.js
// OAuth helpers shared by web and Capacitor native iOS.
//
// Flow (web):
//   1. fetchOAuthUrl() calls our auth endpoint with a Bearer token,
//      which verifies the session and returns the provider consent URL.
//   2. Caller navigates: window.location.href = providerUrl
//
// Flow (native Capacitor):
//   1. fetchOAuthUrl() — same as web, but passes native=1 so the server
//      embeds native:true in the signed state.
//   2. openNativeOAuth(providerUrl) opens SFSafariViewController.
//   3. Server callback redirects to orbit://oauth-callback?provider=...
//   4. iOS fires appUrlOpen; openNativeOAuth() resolves with the result.

import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';

export function isNative() {
  return Capacitor.isNativePlatform();
}

/**
 * Fetch the provider OAuth consent URL from our auth endpoint.
 * Sends the Supabase access token as a Bearer header so the server can
 * derive the user_id without trusting a query param.
 *
 * @param {string} apiPath     e.g. '/api/google/auth'
 * @param {string} accessToken Supabase session access_token
 * @param {{ native?: boolean }} opts
 * @returns {Promise<string>}  Absolute provider consent URL
 */
export async function fetchOAuthUrl(apiPath, accessToken, { native = false, popup = false } = {}) {
  // On native, window.location.origin is orbit://localhost — use the Vercel base URL instead.
  const base = Capacitor.isNativePlatform()
    ? import.meta.env.VITE_API_BASE
    : window.location.origin;
  const url = new URL(apiPath, base);
  if (native) url.searchParams.set('native', '1');
  if (popup) url.searchParams.set('popup', '1');
  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    console.error(`[fetchOAuthUrl] ${res.status} from ${url.toString()}:`, body);
    throw new Error(`oauth_url_fetch_failed:${res.status}`);
  }
  const json = await res.json();
  console.log('[fetchOAuthUrl] got provider URL:', json.url?.slice(0, 60));
  return json.url;
}

/**
 * Open a provider OAuth URL in a popup window and resolve when the popup
 * posts back an orbit_oauth message (via /oauth-popup.html).
 *
 * @param {string} providerUrl  Absolute provider OAuth consent URL
 * @param {{ onSuccess?: Function, onError?: Function, onCancel?: Function }} [cbs]
 */
export function openOAuthPopup(providerUrl, { onSuccess, onError, onCancel } = {}) {
  const w = 520, h = 680;
  const left = Math.round((screen.width - w) / 2);
  const top = Math.round((screen.height - h) / 4);
  const popup = window.open(providerUrl, 'orbit_oauth',
    `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`);

  let settled = false;
  function settle(fn) {
    if (settled) return;
    settled = true;
    window.removeEventListener('message', onMsg);
    clearInterval(closedPoll);
    fn();
  }

  function onMsg(e) {
    if (e.origin !== window.location.origin) return;
    if (e.data?.type !== 'orbit_oauth') return;
    settle(() => e.data.error ? onError?.(e.data.error) : onSuccess?.(e.data));
  }
  window.addEventListener('message', onMsg);

  // Detect user manually closing the popup before completing
  const closedPoll = setInterval(() => {
    if (popup?.closed) settle(() => onCancel?.());
  }, 600);

  return popup;
}

/**
 * Open a provider OAuth URL inside SFSafariViewController and wait for the
 * orbit://oauth-callback deep-link redirect.
 *
 * @param {string} providerUrl  Absolute Google/Microsoft OAuth consent URL
 * @returns {Promise<{ provider: string, status?: string, error?: string }>}
 */
export async function openNativeOAuth(providerUrl) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let appUrlHandle, browserHandle;

    function settle(fn) {
      if (settled) return;
      settled = true;
      appUrlHandle?.then(h => h.remove()).catch(() => {});
      browserHandle?.then(h => h.remove()).catch(() => {});
      fn();
    }

    // Listen for the orbit://oauth-callback deep link
    appUrlHandle = App.addListener('appUrlOpen', ({ url: deepLink }) => {
      if (!deepLink.startsWith('orbit://oauth-callback')) return;
      const params = new URL(deepLink.replace('orbit://', 'https://orbit/')).searchParams;
      settle(() => {
        Browser.close().catch(() => {});
        resolve({
          provider: params.get('provider') || '',
          status: params.get('status') || undefined,
          error: params.get('error') || undefined,
        });
      });
    });

    // User closed the browser without completing OAuth
    browserHandle = Browser.addListener('browserFinished', () => {
      settle(() => reject(new Error('oauth_cancelled')));
    });

    Browser.open({ url: providerUrl, presentationStyle: 'popover' }).catch(err => {
      settle(() => reject(err));
    });
  });
}
